<!-- ── Floating Action Button ─────────────────────────────────────────── -->
<button
  *ngIf="chatEnabled && !hideFab"
  class="ai-fab"
  [class.ai-fab--pulse]="fabPulse"
  [class.ai-fab--blocked]="status.blocked"
  [class.ai-fab--limited]="dailyLimitExhausted && !status.blocked"
  (click)="toggleChat()"
  [title]="'ai.chat.title' | t"
  aria-label="Linc — LumberLinq AI">
  <span class="ai-fab-ring ai-fab-ring--outer" aria-hidden="true"></span>
  <span class="ai-fab-ring ai-fab-ring--inner" aria-hidden="true"></span>
  <div class="ai-fab-core">
    <i class="pi pi-ban"   *ngIf="status.blocked"></i>
    <i class="pi pi-clock" *ngIf="!status.blocked && dailyLimitExhausted"></i>
    <!-- Default/closed-state icon — added 2026-08-22, Phase 1. Uses the Linc AI Help icon since
         Help is the default mode when the widget opens (user's own call when asked which icon
         to show here, since the FAB itself is mode-agnostic before it's opened). -->
    <img class="ai-avatar-icon-img" src="assets/images/ai-chat/linc-ai-help-icon.png" alt="Linc AI"
         *ngIf="!status.blocked && !dailyLimitExhausted">
  </div>
  <span class="ai-fab-name">LINC</span>
</button>

<!-- ── Sidebar Panel ──────────────────────────────────────────────────── -->
<p-drawer
  [(visible)]="chatOpen"
  position="left"
  [style]="{'width': drawerWidthPx + 'px','max-width':'100vw','padding':'0'}"
  styleClass="ai-chat-sidebar"
  [showCloseIcon]="false"
  [transitionOptions]="'0ms'"
  (onHide)="onChatDrawerHide()"
  appendTo="body">

  <!-- Resize handle — drag to widen/narrow the panel (hidden on mobile via CSS, same
       max-width:420px breakpoint used elsewhere in this widget). Fixed positioning so it always
       sits at the panel's real right edge regardless of where PrimeNG places projected content. -->
  <div class="ai-resize-handle"
       *ngIf="chatOpen"
       [class.ai-resize-handle--active]="isResizingDrawer"
       [style.left.px]="drawerWidthPx"
       (mousedown)="onResizeStart($event)"
       (touchstart)="onResizeStart($event)">
    <!-- Grip — added 2026-08-23, purely a visual affordance so the resizable edge is
         discoverable at a glance, not just on accidental hover. pointer-events:none so drags
         still land on the wider .ai-resize-handle strip behind it, never on the grip itself. -->
    <div class="ai-resize-grip" aria-hidden="true">
      <span></span><span></span><span></span>
    </div>
  </div>

  <!-- ── Header ──────────────────────────────────────────────────────── -->
  <!-- Rebuilt 2026-08-22 — pill-card header (light card + pastel wash, real logo icon at full
       size, single title line, status dot + kebab menu) matching the approved design preview.
       Every existing binding/handler below is unchanged from before this pass — only the
       layout/classes moved. -->
  <ng-template pTemplate="header">
    <div class="ai-header">

      <!-- Disclaimer gate header — simplified, no mode switcher/status while the combined
           Linc + "Linc Data" disclaimer hasn't been accepted yet (see AiDisclaimerService). -->
      <div class="ai-header-inner" *ngIf="disclaimerAccepted === false">
        <div class="ai-header-left">
          <div class="ai-avatar-wrap">
            <div class="ai-avatar-bot"><i class="pi pi-shield"></i></div>
          </div>
          <div class="ai-header-text">
            <div class="ai-header-name-row">
              <span class="ai-header-title">Before You Continue</span>
            </div>
          </div>
        </div>
        <div class="ai-header-actions">
          <button pButton icon="pi pi-times" class="p-button-text p-button-sm ai-icon-btn"
            pTooltip="Close" tooltipPosition="bottom"
            (click)="chatOpen = false">
          </button>
        </div>
      </div>

      <div class="ai-header-inner" *ngIf="disclaimerAccepted">
        <div class="ai-header-wash" [class.ai-header-wash--data]="mode === 'data'" aria-hidden="true"></div>

        <div class="ai-header-left">
          <!-- Avatar — real logo icon at full size, no circular backing (status now shown via
               the dot next to the kebab menu, not a ring around the icon). -->
          <div class="ai-avatar-wrap">
            <div class="ai-avatar-bot">
              <img class="ai-avatar-icon-img" src="assets/images/ai-chat/linc-ai-help-icon.png" alt="Linc AI Help"
                   *ngIf="mode === 'kb' && !status.blocked && !dailyLimitExhausted">
              <i class="pi pi-ban"      *ngIf="mode === 'kb' && status.blocked"></i>
              <i class="pi pi-clock"    *ngIf="mode === 'kb' && !status.blocked && dailyLimitExhausted"></i>
              <img class="ai-avatar-icon-img" src="assets/images/ai-chat/linc-ai-assistant-icon.png" alt="Linc AI Assistant"
                   *ngIf="mode === 'data'">
              <img class="ai-avatar-icon-img" src="assets/images/ai-chat/linc-ai-help-icon.png" alt="Linc AI"
                   *ngIf="mode === 'auto'">
            </div>
          </div>

          <!-- Name + status — single title line ("Linc AI Help" / "Linc AI Assistant"), subtitle
               swaps between the tagline and the real status text (was a separate dot inline
               here; the dot moved to the actions side, see below). -->
          <div class="ai-header-text">
            <div class="ai-header-name-row">
              <span class="ai-header-title">{{ mode === 'data' ? 'Linc AI Assistant' : (mode === 'auto' ? ('ai.chat.titleAuto' | t) : 'Linc AI Help') }}</span>
            </div>
            <span class="ai-header-sub"
                  [class.ai-header-sub--thinking]="isBusy"
                  [class.ai-header-sub--error]="mode === 'kb' && (status.blocked || dailyLimitExhausted)">
              <span class="ai-header-sub-text">
                <ng-container *ngIf="mode === 'kb' && status.blocked">Temporarily blocked</ng-container>
                <ng-container *ngIf="mode === 'kb' && !status.blocked && dailyLimitExhausted">Daily limit reached</ng-container>
                <ng-container *ngIf="(mode !== 'kb' || (!status.blocked && !dailyLimitExhausted)) && isBusy">{{ mode === 'data' ? 'Thinking…' : ('ai.chat.thinking' | t) }}</ng-container>
                <ng-container *ngIf="mode === 'data' && !isBusy">Your Personal AI Assistant</ng-container>
                <ng-container *ngIf="mode === 'auto' && !isBusy">{{ 'ai.chat.autoSub' | t }}</ng-container>
                <ng-container *ngIf="mode === 'kb' && !status.blocked && !dailyLimitExhausted && !isBusy">Ask Anything · Get Step-by-Step Help</ng-container>
              </span>
            </span>
          </div>
        </div>

        <!-- Status dot + kebab menu — replaces the old 4 always-visible icon buttons. Same 3
             remaining actions (history, sound, email transcript), reached through one menu (a
             p-popover, same pattern already used for the emoji panel below in this file). Close
             was originally folded into this same menu, but pulled back out into its own always-
             visible button below — added 2026-08-23. Close is a different KIND of action from the
             other 3 (exit the whole widget vs. do something while using it), it's the single
             highest-frequency action in the widget, and burying the most common action behind an
             extra tap while the occasional ones stay one tap away gets the priority backwards —
             also now matches the standalone X the disclaimer-gate header above already has. -->
        <div class="ai-header-actions">
          <span class="ai-status-dot"
                [class.ai-status-dot--ok]="!isBusy && !(mode === 'kb' && (status.blocked || dailyLimitExhausted))"
                [class.ai-status-dot--thinking]="isBusy"
                [class.ai-status-dot--error]="mode === 'kb' && (status.blocked || dailyLimitExhausted)"
                aria-hidden="true"></span>
          <button class="ai-header-kebab" (click)="headerMenuPanel.toggle($event)" title="More" aria-label="More actions">
            <i class="pi pi-ellipsis-h"></i>
          </button>
          <button class="ai-header-kebab" (click)="chatOpen = false" title="Close" aria-label="Close">
            <i class="pi pi-times"></i>
          </button>
        </div>
      </div>

      <!-- Mode switcher — only exists for users who pass BOTH gates (Forest-tier plan AND the
           DATA_ASSISTANT_ACCESS permission). Zero markup rendered for anyone else, so a regular
           user's widget is pixel-identical to before this feature existed. -->
      <div class="ai-mode-switch" *ngIf="disclaimerAccepted && showModeSwitch" role="tablist" aria-label="Assistant mode"
           [style.--ai-tabs]="modeTabCount" [style.--ai-tab-idx]="modeTabIndex">
        <button type="button" class="ai-mode-btn" *ngIf="autoAvailable" [class.ai-mode-btn--active]="mode === 'auto'"
                role="tab" [attr.aria-selected]="mode === 'auto'" (click)="setMode('auto')">
          <i class="pi pi-bolt"></i> {{ 'ai.chat.modeAuto' | t }}
        </button>
        <button type="button" class="ai-mode-btn" [class.ai-mode-btn--active]="mode === 'kb'"
                role="tab" [attr.aria-selected]="mode === 'kb'" (click)="setMode('kb')">
          <i class="pi pi-comments"></i> Help
        </button>
        <button type="button" class="ai-mode-btn" *ngIf="canUseDataMode" [class.ai-mode-btn--active]="mode === 'data'"
                role="tab" [attr.aria-selected]="mode === 'data'" (click)="setMode('data')">
          <i class="pi pi-database"></i> Assistant
        </button>
        <span class="ai-mode-slider" [class.ai-mode-slider--data]="mode === 'data'" aria-hidden="true"></span>
      </div>

      <!-- Usage row — merged 2026-08-22 into one row (was two separate stacked rows, which the
           approved design preview never modeled — it only ever showed one usage line per mode).
           Daily bar (Linc/KB mode only — "Linc Data" is metered by AI credits instead, shown
           inline on a credit-exhausted answer rather than a persistent bar) on the left; AI
           credits (see the note below on why this is unconditional on mode) on the right.
           flex-wrap so it degrades to two lines gracefully on a genuinely narrow drawer instead
           of overlapping, rather than hand-tuning an exact-fit width. "Online · by LumberLinq"
           from the preview was dropped rather than force-fit — the header's own status dot
           already says "online", so repeating the word here added nothing; kept "by LumberLinq"
           as a small credit instead. -->
      <div class="ai-usage-row" *ngIf="disclaimerAccepted && ((mode === 'kb' && chatEnabled && !status.blocked && status.dailyLimit > 0) || aiCreditsUsage)">
        <div class="ai-daily-bar" *ngIf="mode === 'kb' && chatEnabled && !status.blocked && status.dailyLimit > 0">
          <div class="ai-daily-track">
            <div class="ai-daily-fill" [class]="dailyProgressClass" [style.width.%]="dailyLimitPercent"></div>
          </div>
          <span class="ai-daily-label" [class]="dailyProgressClass">
            {{ status.dailyCount }}/{{ status.dailyLimit }} messages today
          </span>
        </div>

        <!-- AI Credits — added 2026-08-22, Phase 4. Deliberately NEUTRAL styling even when
             aiCreditsUsage.atLimit is true — aiPageCredits defaults to 0 for tenants that never
             bought AI credits, so an alarm style would falsely look like an error for most
             chat-only tenants. This is a read-only number — nothing about chat is actually
             blocked by it yet. Separate call from the daily-count status above
             (refreshAiCreditsUsage(), not refreshStatus()), on purpose — see the component's own
             note. Unconditional on mode (real bug fixed 2026-08-22): aiCreditsUsage is a UNIFIED
             total across every AI feature (featureKey "ai_import" — the same shared credit pool
             KB chat, Linc Data, and AI Import all draw from, confirmed via
             refreshAiCreditsUsage() above, which loads it unconditionally in ngOnInit regardless
             of mode) — gating this to mode === 'kb' was simply wrong. -->
        <!-- Tappable — added 2026-08-22, opens a tiny real breakdown (not decorative — every
             number here is the same live data already shown elsewhere, just laid out together).
             Reward bump sits right next to it: a brief "+10" the moment a feedback reward lands,
             see submitDataFeedback()'s own note on why it can't double-fire. -->
        <span class="ai-header-credits" *ngIf="aiCreditsUsage"
              (click)="showCreditsBreakdown = !showCreditsBreakdown" tabindex="0"
              (keydown.enter)="showCreditsBreakdown = !showCreditsBreakdown">
          {{ aiCreditsUsage.used }}{{ aiCreditsUsage.limit >= 0 ? ' / ' + aiCreditsUsage.limit : '' }} AI credits &middot; by LumberLinq
          <span class="ai-reward-bump" *ngIf="rewardBumpAmount">+{{ rewardBumpAmount }} credits back!</span>
        </span>
      </div>
      <div class="ai-credits-breakdown" *ngIf="showCreditsBreakdown && aiCreditsUsage">
        <span>{{ aiCreditsUsage.used }} credits used so far</span>
        <span *ngIf="aiCreditsUsage.limit >= 0">{{ aiCreditsUsage.limit - aiCreditsUsage.used }} remaining of {{ aiCreditsUsage.limit }}</span>
        <span>Thumbs up/down on an answer gives some credits back — see the "+" pop next to the number above when it happens.</span>
      </div>
    </div>

    <!-- Header kebab menu — the remaining 3 actions that used to be always-visible buttons.
         Close used to be the 4th item here — moved out to its own standalone button next to this
         menu (see ai-header-actions above) since 2026-08-23; not duplicated here anymore. -->
    <p-popover #headerMenuPanel styleClass="ai-header-menu-panel">
      <div class="ai-header-menu">
        <button class="ai-header-menu-item" *ngIf="mode !== 'auto'"
                (click)="promptEmailTranscript(); headerMenuPanel.hide()"
                [disabled]="mode === 'data' ? !dataSessionId : !currentSessionId">
          <i class="pi pi-envelope"></i> Email transcript
        </button>
        <button class="ai-header-menu-item" (click)="newSession(); headerMenuPanel.hide()">
          <i class="pi pi-plus"></i> New chat
        </button>
        <button class="ai-header-menu-item" *ngIf="mode !== 'auto'" (click)="toggleHistory(); headerMenuPanel.hide()">
          <i class="pi pi-history"></i> Chat history
        </button>
      </div>
    </p-popover>
  </ng-template>

  <!-- ── Body ────────────────────────────────────────────────────────── -->
  <div class="ai-chat-body">

    <!-- ── Combined AI disclaimer gate — covers BOTH Linc and "Linc Data" (one acceptance, see
         AiDisclaimerService's own note). Blocks sending in either mode until accepted; recorded
         server-side (user, version, time, IP, browser) so it's real, provable consent, not just a
         UI flag. Nothing else in this drawer renders until disclaimerAccepted is resolved. Text
         below mirrors exception.md verbatim (the stronger, numbered-clause legal version) — keep
         both in sync if either is ever edited; only "My Data" was renamed to "Linc Data" here. ── -->
    <div class="ai-disclaimer-gate" *ngIf="disclaimerAccepted === false">
      <div class="ai-disclaimer-gate-icon"><i class="pi pi-shield"></i></div>
      <div class="ai-disclaimer-gate-title">Before You Use LumberLinq AI</div>
      <div class="ai-disclaimer-gate-text">
        <p>
          LumberLinq AI, including "Linc AI Help" and "Linc AI Assistant", uses artificial intelligence and
          automated systems to generate responses. Please read and accept the following before
          continuing.
        </p>
        <ol>
          <li>
            <strong>AI-generated information may be inaccurate.</strong> Responses generated by
            LumberLinq AI may be incorrect, incomplete, misleading, unavailable, or out of date.
            This includes, without limitation, information relating to stock, inventory, shipments,
            payments, invoices, accounts, totals, calculations, dates, customers, suppliers,
            business partners, or any other data. You are solely responsible for independently
            verifying all information against the relevant records and documents before relying on
            it or taking any action.
          </li>
          <li>
            <strong>Do not rely on AI as your sole source of information.</strong> LumberLinq AI is
            provided as an assistance and productivity tool only. Any response generated by the AI
            should not be treated as a final, authoritative, or verified record. The official
            records available within LumberLinq and your underlying business documents remain your
            responsibility to review and verify.
          </li>
          <li>
            <strong>No professional advice.</strong> LumberLinq AI does not provide legal,
            financial, tax, accounting, investment, compliance, medical, or other professional
            advice. You should consult an appropriately qualified professional before making any
            important business or other decision.
          </li>
          <li>
            <strong>"Linc AI Assistant" uses your company's records.</strong> When using "Linc AI Assistant",
            LumberLinq AI may retrieve, process, summarize, analyse, or use information from your
            company's account to generate a response. Access to information is subject to the
            permissions and access controls configured for your account. You must not attempt to
            access, request, or use information that you are not authorized to access.
          </li>
          <li>
            <strong>Your questions may be processed by third-party AI providers.</strong> To
            generate responses, the text and information submitted through LumberLinq AI may be
            transmitted to and processed by third-party artificial intelligence service providers,
            which may include OpenAI, Anthropic, Google, or other providers configured by RIK Exim
            Private Limited from time to time. Do not enter passwords, card details, government
            identification numbers, authentication credentials, or other highly sensitive
            information unless expressly permitted by LumberLinq.
          </li>
          <li>
            <strong>AI responses are provided "AS IS".</strong> To the maximum extent permitted by
            applicable law, RIK Exim Private Limited and LumberLinq make no representation,
            warranty, or guarantee — express or implied — regarding the accuracy, completeness,
            reliability, availability, suitability, security, or fitness for a particular purpose of
            any AI-generated response.
          </li>
          <li>
            <strong>You are responsible for your decisions.</strong> Any action you take, or
            decision you make, based wholly or partly on information generated by LumberLinq AI is
            made at your own discretion and risk. You remain solely responsible for verifying
            information and for all resulting business, financial, operational, legal, or other
            decisions.
          </li>
          <li>
            <strong>Limitation of liability.</strong> To the maximum extent permitted by applicable
            law, RIK Exim Private Limited, its directors, officers, employees, affiliates,
            licensors, and service providers shall not be liable for any direct, indirect,
            incidental, consequential, special, exemplary, or other loss, damage, cost, claim,
            liability, or expense arising from or relating to your use of, reliance upon, or
            inability to use LumberLinq AI or any AI-generated response.
          </li>
        </ol>
        <p class="ai-disclaimer-gate-footer-note">
          By checking the box below and continuing, you confirm that you have read, understood, and
          accepted the above terms.
        </p>
      </div>
    </div>

    <ng-container *ngIf="disclaimerAccepted">

    <!-- Blocked banner (Linc/KB mode only) -->
    <div class="ai-rate-banner ai-rate-banner--blocked" *ngIf="mode === 'kb' && status.blocked">
      <div class="ai-rate-banner-icon"><i class="pi pi-ban"></i></div>
      <div class="ai-rate-banner-content">
        <div class="ai-rate-banner-title">Temporarily Blocked</div>
        <div class="ai-rate-banner-msg">{{ status.blockedMessage }}</div>
      </div>
    </div>

    <!-- Daily limit banner (Linc/KB mode only) -->
    <div class="ai-rate-banner ai-rate-banner--limit" *ngIf="mode === 'kb' && !status.blocked && dailyLimitExhausted">
      <div class="ai-rate-banner-icon"><i class="pi pi-clock"></i></div>
      <div class="ai-rate-banner-content">
        <div class="ai-rate-banner-title">Daily Limit Reached</div>
        <div class="ai-rate-banner-msg">
          You've used all {{ status.dailyLimit }} messages today.
          Resets at midnight — upgrade your plan for a higher daily limit.
        </div>
      </div>
    </div>

    <!-- History panel — Linc/KB mode. Was gated on sessions.length > 0 alone, so a click that
         found zero sessions (nothing loaded yet, or a genuinely new user) rendered nothing at
         all — same "dead button" symptom the toggleHistory() fix above addresses server-side;
         this empty-state covers the client-side half of it. -->
    <div class="ai-history-panel" *ngIf="mode === 'kb' && historyOpen">
      <div class="ai-history-header">
        <i class="pi pi-history"></i> Past Conversations
      </div>
      <div class="ai-history-item"
           *ngFor="let s of sessions"
           [class.ai-history-item--active]="s.id === currentSessionId"
           (click)="loadSession(s.id)">
        <i class="pi pi-comments" style="font-size:.8rem;opacity:.45;flex-shrink:0"></i>
        <span class="ai-history-title">{{ s.title }}</span>
        <button class="ai-history-del" (click)="deleteSession(s.id, $event)" title="Delete">
          <i class="pi pi-trash"></i>
        </button>
      </div>
      <div class="ai-history-empty" *ngIf="sessions.length === 0">No past conversations yet.</div>
    </div>

    <!-- History panel — "Linc Data" mode (same table AiDataAssistantService.ask() already writes
         for audit — see AiDataAssistantHistoryService, no second store) -->
    <div class="ai-history-panel" *ngIf="mode === 'data' && historyOpen">
      <div class="ai-history-header">
        <i class="pi pi-history"></i> Past Conversations
      </div>
      <div class="ai-history-item"
           *ngFor="let s of dataSessions"
           [class.ai-history-item--active]="s.sessionId === dataSessionId"
           (click)="loadDataSession(s.sessionId)">
        <i class="pi pi-database" style="font-size:.8rem;opacity:.45;flex-shrink:0"></i>
        <span class="ai-history-title">{{ s.title }}</span>
        <button class="ai-history-del" (click)="deleteDataSession(s.sessionId, $event)" title="Delete">
          <i class="pi pi-trash"></i>
        </button>
      </div>
      <div class="ai-history-empty" *ngIf="dataSessions.length === 0">No past conversations yet.</div>
    </div>

    <!-- ── Welcome screen — Linc/KB mode ── -->
    <div class="ai-welcome" *ngIf="mode === 'kb' && messages.length === 0 && !loading && !status.blocked && !dailyLimitExhausted">

      <div class="ai-welcome-avatar-wrap">
        <div class="ai-welcome-orb">
          <img class="ai-welcome-icon-img" src="assets/images/ai-chat/linc-ai-help-icon.png" alt="Linc AI Help">
        </div>
        <span class="ai-welcome-sparkle ai-welcome-sparkle--1" aria-hidden="true">✦</span>
        <span class="ai-welcome-sparkle ai-welcome-sparkle--2" aria-hidden="true">✦</span>
        <span class="ai-welcome-sparkle ai-welcome-sparkle--3" aria-hidden="true">✦</span>
      </div>

      <!-- "AI Assistant" wording changed to "Help Guide" here (2026-08-22) — "Assistant" is now
           the OTHER mode's name (Linc AI Assistant), keeping this distinct avoids the two modes
           sounding like the same thing. -->
      <div class="ai-welcome-greeting">Hi, I'm Linc AI Help 👋</div>
      <div class="ai-welcome-title">Your LumberLinq Help Guide</div>
      <div class="ai-welcome-desc">
        Ask me about tally sheets, shipments, inventory, reports, and any LumberLinq feature.
        I'll guide you step by step.
      </div>

      <div class="ai-disclaimer-note">
        <i class="pi pi-shield"></i>
        {{ 'ai.chat.disclaimer' | t }}
      </div>

      <div class="ai-starters">
        <button class="ai-starter-chip" [disabled]="loading" (click)="sendStarter('How do I create a tally sheet?')">
          <i class="pi pi-file-plus"></i> Create tally sheet
        </button>
        <button class="ai-starter-chip" [disabled]="loading" (click)="sendStarter('How do I add a new shipment?')">
          <i class="pi pi-truck"></i> Add shipment
        </button>
        <button class="ai-starter-chip" [disabled]="loading" (click)="sendStarter('How do I export a tally report?')">
          <i class="pi pi-download"></i> Export report
        </button>
        <button class="ai-starter-chip" [disabled]="loading" (click)="sendStarter('What is a Stock Unit?')">
          <i class="pi pi-box"></i> What is a Stock Unit?
        </button>
        <button class="ai-starter-chip" [disabled]="loading" (click)="sendStarter('How do I share a tally with a buyer?')">
          <i class="pi pi-share-alt"></i> Share tally
        </button>
        <button class="ai-starter-chip" [disabled]="loading" (click)="sendStarter('How do I invite a team member?')">
          <i class="pi pi-user-plus"></i> Invite team
        </button>
      </div>
    </div>

    <!-- ── Welcome screen — "Linc Data" mode ── -->
    <div class="ai-welcome" *ngIf="mode === 'data' && dataMessages.length === 0 && !dataLoading">

      <div class="ai-welcome-avatar-wrap">
        <div class="ai-welcome-orb ai-welcome-orb--data">
          <img class="ai-welcome-icon-img" src="assets/images/ai-chat/linc-ai-assistant-icon.png" alt="Linc AI Assistant">
        </div>
        <span class="ai-welcome-sparkle ai-welcome-sparkle--1" aria-hidden="true">✦</span>
        <span class="ai-welcome-sparkle ai-welcome-sparkle--2" aria-hidden="true">✦</span>
        <span class="ai-welcome-sparkle ai-welcome-sparkle--3" aria-hidden="true">✦</span>
      </div>

      <div class="ai-welcome-greeting">Linc AI Assistant 📊</div>
      <div class="ai-welcome-title">Ask about your company's own live data</div>
      <div class="ai-welcome-desc">
        Stock, shipments, payments, business partners, and more — I'll answer straight from your
        real numbers, not the knowledge base.
      </div>

      <div class="ai-disclaimer-note">
        <i class="pi pi-shield"></i>
        Answers are based on your company's own live LumberLinq data and may not always be fully accurate.
      </div>

      <div class="ai-starters">
        <button class="ai-starter-chip" *ngFor="let s of dataStarters" [disabled]="dataLoading" (click)="sendStarter(starterQuestion(s.qKey))">
          <i [class]="s.icon"></i> {{ s.qKey | t }}
        </button>
      </div>
    </div>

    <!-- ── Messages — Linc/KB mode ── -->
    <!-- Fixed 2026-08-23 — same bug/fix as the "Linc Data" mode messages list below: was
         *ngIf="mode === 'kb'" only, so this (flex:1, stretches to fill space) rendered empty at
         the same time as the welcome screen (and the blocked/limit banners), squeezing them into
         a small area instead of letting them fill the height. Checked the blocked/limit banners
         specifically before applying this here — they're each their own independent block, so
         this same simple fix is safe for them too, not just the welcome screen. -->
    <div class="ai-messages" #messagesContainer *ngIf="mode === 'kb' && messages.length > 0">
      <div *ngFor="let msg of messages; let i = index; trackBy: trackByMsgId"
           class="ai-msg-wrapper"
           [class.ai-msg-wrapper--user]="msg.role === 'user'"
           [class.ai-msg-wrapper--bot]="msg.role === 'assistant'"
           [style.display]="msg.content?.trim() ? null : 'none'"
           [style.animation-delay.ms]="i * 25">
        <!-- display:none guard: belt-and-suspenders against ever rendering a shaped, empty
             bubble (see _doSend()'s own content check for the real fix at the source).
             Hidden via style, not filtered from the array, so msgSources[i] stays aligned. -->

        <!-- Bot avatar -->
        <div class="ai-avatar-bot-sm" *ngIf="msg.role === 'assistant'">
          <img class="ai-avatar-icon-img" src="assets/images/ai-chat/linc-ai-help-icon.png" alt="Linc AI Help">
        </div>

        <!-- Bubble -->
        <div class="ai-msg-bubble"
             [class.ai-msg-bubble--user]="msg.role === 'user'"
             [class.ai-msg-bubble--bot]="msg.role === 'assistant'">

          <!-- Attachment tag -->
          <div class="ai-attachment-preview" *ngIf="msg.attachmentName">
            <i class="pi pi-file"></i>
            <span>{{ msg.attachmentName }}</span>
          </div>

          <!-- Message body -->
          <div class="ai-msg-text" [innerHTML]="formatMessage(msg.content)"></div>

          <!-- Meta line — timestamp + thumbs on one line by default (see .ai-msg-meta /
               .ai-fb-btn-inline in the CSS); browser's own local time, i.e. the user's own
               timezone, no conversion needed since Angular's date pipe formats in local time
               by default -->
          <div class="ai-msg-meta">
            <span class="ai-msg-time">{{ formatMsgTime(msg.createdAt) }}</span>
            <!-- Credits used — added 2026-08-22, same "exactly like Assistant" treatment: only
                 shown when this reply actually cost something (null/undefined for a free
                 greeting or acknowledgment reply — see AiChatService's own note). -->
            <ng-container *ngIf="msg.role === 'assistant' && msg.creditsUsed">
              <span class="ai-msg-meta-sep">&middot;</span>
              <span class="ai-credits-used">{{ msg.creditsUsed }} credit{{ msg.creditsUsed === 1 ? '' : 's' }} used</span>
            </ng-container>
            <ng-container *ngIf="msg.role === 'assistant' && msg.id">
              <span class="ai-msg-meta-sep">&middot;</span>
              <span class="ai-feedback--inline">
                <button class="ai-fb-btn-inline"
                        [class.ai-fb-btn-inline--active]="feedbackMap[msg.id] === 1"
                        (click)="submitFeedback(msg.id!, 1)"
                        title="Helpful">
                  <i class="pi pi-thumbs-up"></i>
                </button>
                <button class="ai-fb-btn-inline"
                        [class.ai-fb-btn-inline--active]="feedbackMap[msg.id] === -1"
                        (click)="submitFeedback(msg.id!, -1)"
                        title="Not helpful">
                  <i class="pi pi-thumbs-down"></i>
                </button>
              </span>
            </ng-container>
          </div>

          <!-- "Why" reason chips — appear only after a thumbs-down, one-tap optional, see
               selectFeedbackReason()'s own note. Never shown on thumbs-up. -->
          <div class="ai-fb-reason-row" *ngIf="activeReasonPromptId === msg.id">
            <button class="ai-fb-reason-chip" *ngFor="let r of feedbackReasons"
                    [class.ai-fb-reason-chip--active]="reasonMap[msg.id!] === r.code"
                    (click)="selectFeedbackReason(msg.id!, r.code)">
              {{ r.label }}
            </button>
          </div>

          <!-- KB sources -->
          <div class="ai-sources" *ngIf="msg.role === 'assistant' && msgSources[i]?.length">
            <span class="ai-sources-label">{{ 'ai.chat.sources' | t }}:</span>
            <span class="ai-source-chip" *ngFor="let src of msgSources[i]">
              <i class="pi pi-database" style="font-size:.58rem"></i> {{ src }}
            </span>
          </div>

          <!-- Go to screen — only when the answer points at a screen this person may open -->
          <button class="ai-view-details-btn" *ngIf="msg.role === 'assistant' && msg.deepLink"
                  (click)="goToDeepLink(msg.deepLink)">
            <span>{{ 'ai.chat.goToScreen' | t }}</span>
            <i class="pi pi-arrow-right"></i>
          </button>

          <!-- "You might also ask" — KB/Help mode equivalent of the "Linc Data" block below. Reuses
               the same .ai-related-questions* classes (shared by both modes already). -->
          <div class="ai-related-questions" *ngIf="msg.role === 'assistant' && msg.relatedQuestions?.length">
            <span class="ai-related-questions-label">You might also ask</span>
            <button class="ai-related-question-chip" *ngFor="let q of msg.relatedQuestions" [disabled]="loading"
                    (click)="askKbRelatedQuestion(q)">
              <i class="pi pi-plus-circle"></i>
              <span>{{ q }}</span>
            </button>
          </div>

          <!-- Feedback — small-screen row fallback only (hidden by default, see the
               max-width:420px media query in the CSS); same handlers as the inline version above -->
          <div class="ai-feedback" *ngIf="msg.role === 'assistant' && msg.id">
            <button class="ai-fb-btn"
                    [class.ai-fb-btn--active]="feedbackMap[msg.id] === 1"
                    (click)="submitFeedback(msg.id!, 1)"
                    title="Helpful">
              <i class="pi pi-thumbs-up"></i>
            </button>
            <button class="ai-fb-btn"
                    [class.ai-fb-btn--active]="feedbackMap[msg.id] === -1"
                    (click)="submitFeedback(msg.id!, -1)"
                    title="Not helpful">
              <i class="pi pi-thumbs-down"></i>
            </button>
          </div>
          <div class="ai-fb-reason-row" *ngIf="activeReasonPromptId === msg.id">
            <button class="ai-fb-reason-chip" *ngFor="let r of feedbackReasons"
                    [class.ai-fb-reason-chip--active]="reasonMap[msg.id!] === r.code"
                    (click)="selectFeedbackReason(msg.id!, r.code)">
              {{ r.label }}
            </button>
          </div>
        </div>

        <!-- User avatar — real profile photo when set, else the real first-letter initial -->
        <ng-container *ngIf="msg.role === 'user'">
          <div class="ai-avatar-user ai-avatar-user--photo" *ngIf="getAvatarImgSrc() as avatarSrc; else userInitialAvatar">
            <img [src]="avatarSrc" alt="You">
          </div>
          <ng-template #userInitialAvatar>
            <div class="ai-avatar-user">{{ userInitial }}</div>
          </ng-template>
        </ng-container>
      </div>

      <!-- Typing indicator -->
      <div class="ai-msg-wrapper ai-msg-wrapper--bot" *ngIf="loading">
        <div class="ai-avatar-bot-sm ai-avatar-bot-sm--breathing">
          <img class="ai-avatar-icon-img" src="assets/images/ai-chat/linc-ai-help-icon.png" alt="Linc AI Help">
        </div>
        <div class="ai-msg-bubble ai-msg-bubble--bot ai-typing">
          <span class="ai-dot"></span>
          <span class="ai-dot"></span>
          <span class="ai-dot"></span>
        </div>
      </div>
    </div>

    <!-- ── Messages — "Linc Data" mode ── -->
    <!-- Fixed 2026-08-23 — was *ngIf="mode === 'data'" only, with no check for whether there were
         any messages yet. Since the welcome screen above already shows whenever
         dataMessages.length === 0, this element (flex:1, stretches to fill space) was ALSO
         rendering at the same time, empty, squeezing the welcome screen into a small area instead
         of letting it fill the height. Added the same "only when there's something to show"
         condition the welcome screen already uses, so exactly one of the two is ever in the DOM. -->
    <div class="ai-messages" #messagesContainer *ngIf="mode === 'data' && dataMessages.length > 0">
      <div *ngFor="let msg of dataMessages; let i = index; trackBy: trackByMsgId"
           class="ai-msg-wrapper"
           [class.ai-msg-wrapper--user]="msg.role === 'user'"
           [class.ai-msg-wrapper--bot]="msg.role === 'assistant'"
           [style.display]="msg.content?.trim() ? null : 'none'"
           [style.animation-delay.ms]="i * 25">

        <!-- Bot avatar — distinct "data" accent so it's visually obvious this came from live
             data, not the knowledge base -->
        <div class="ai-avatar-bot-sm ai-avatar-bot-sm--data" *ngIf="msg.role === 'assistant'">
          <img class="ai-avatar-icon-img" src="assets/images/ai-chat/linc-ai-assistant-icon.png" alt="Linc AI Assistant">
        </div>

        <!-- Bubble -->
        <div class="ai-msg-bubble ai-msg-bubble--data"
             [class.ai-msg-bubble--user]="msg.role === 'user'"
             [class.ai-msg-bubble--bot]="msg.role === 'assistant'">

          <!-- Message body — added 2026-08-23: renders whichever shape getFormattedAnswer()
               decided at message-construction time (see msg.formatted). 'text' is the exact same
               formatMessage() rendering as before; the other 3 are purely additive presentations
               of the same content/data, chosen automatically by the data's own shape. -->
          <div class="ai-msg-text" [ngSwitch]="msg.formatted.kind">

            <div *ngSwitchCase="'text'" [innerHTML]="msg.formatted.html"></div>

            <div *ngSwitchCase="'table'" class="ai-ans-table-wrap">
              <p-table [value]="msg.formatted.rows" [columns]="msg.formatted.columns" styleClass="ai-ans-table">
                <ng-template pTemplate="header" let-columns>
                  <tr>
                    <th *ngFor="let col of columns">{{ col.label }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row let-rowIndex="rowIndex" let-columns="columns">
                  <tr [style.animation-delay.ms]="rowIndex * 40">
                    <td *ngFor="let col of columns">{{ row[col.key] }}</td>
                  </tr>
                </ng-template>
              </p-table>
            </div>

            <div *ngSwitchCase="'ranked'" class="ai-ans-ranked">
              <div class="ai-ans-ranked-row" *ngFor="let item of msg.formatted.items; let j = index"
                   [style.animation-delay.ms]="j * 40">
                <p-badge [value]="item.rank" severity="secondary"></p-badge>
                <span class="ai-ans-ranked-name">{{ item.companyName }}</span>
                <p-tag *ngIf="item.onTimeLabel" [value]="item.onTimeLabel" [severity]="item.onTimeSeverity ?? undefined"></p-tag>
                <p-tag *ngIf="item.lossLabel" [value]="item.lossLabel" [severity]="item.lossSeverity ?? undefined"></p-tag>
                <span class="ai-ans-ranked-extra" *ngIf="item.outturnLabel">{{ item.outturnLabel }}</span>
                <span class="ai-ans-ranked-extra">{{ item.shipmentCount }} shipment{{ item.shipmentCount === 1 ? '' : 's' }}</span>
              </div>
            </div>

            <div *ngSwitchCase="'stat'" class="ai-ans-stats">
              <div class="ai-ans-stat-tile" *ngFor="let item of msg.formatted.items; let j = index"
                   [style.animation-delay.ms]="j * 40">
                <div class="ai-ans-stat-value">{{ item.value }}</div>
                <div class="ai-ans-stat-label">{{ item.label }}</div>
              </div>
            </div>

          </div>

          <!-- Meta line — timestamp + credits used + thumbs, all on one line by default (see
               .ai-msg-meta / .ai-fb-btn-inline in the CSS). Credits used — added 2026-08-22,
               Phase 2d — only shown when this turn actually cost something (null, not shown, for
               a free greeting/disambiguation-resume answer). Feedback — added 2026-08-22. Only on
               a real, resolved answer: an unresolved disambiguation/clarification turn has
               nothing to rate yet, and a turn whose log row failed to save has no queryLogId to
               send. -->
          <div class="ai-msg-meta">
            <span class="ai-msg-time">{{ formatMsgTime(msg.createdAt) }}</span>
            <ng-container *ngIf="msg.role === 'assistant' && msg.creditsUsed">
              <span class="ai-msg-meta-sep">&middot;</span>
              <span class="ai-credits-used" [class.ai-credits-pulse]="msg.creditsAnimating">{{ msg.creditsUsed }} credit{{ msg.creditsUsed === 1 ? '' : 's' }} used<span class="ai-reward-float" *ngIf="msg.creditsRewardBadgeAmount">+{{ msg.creditsRewardBadgeAmount }} credits back &#10024;</span></span>
            </ng-container>
            <ng-container *ngIf="msg.role === 'assistant' && msg.resolved && msg.queryLogId">
              <span class="ai-msg-meta-sep">&middot;</span>
              <span class="ai-feedback--inline">
                <button class="ai-fb-btn-inline"
                        [class.ai-fb-btn-inline--active]="dataFeedbackMap[msg.queryLogId!] === 1"
                        (click)="submitDataFeedback(msg.queryLogId!, 1)"
                        title="Helpful">
                  <i class="pi pi-thumbs-up"></i>
                </button>
                <button class="ai-fb-btn-inline"
                        [class.ai-fb-btn-inline--active]="dataFeedbackMap[msg.queryLogId!] === -1"
                        (click)="submitDataFeedback(msg.queryLogId!, -1)"
                        title="Not helpful">
                  <i class="pi pi-thumbs-down"></i>
                </button>
              </span>
            </ng-container>
          </div>

          <!-- "Why" reason chips — appear only after a thumbs-down, one-tap optional, see
               selectDataFeedbackReason()'s own note. Never shown on thumbs-up. -->
          <div class="ai-fb-reason-row" *ngIf="activeDataReasonPromptId === msg.queryLogId">
            <button class="ai-fb-reason-chip" *ngFor="let r of feedbackReasons"
                    [class.ai-fb-reason-chip--active]="dataReasonMap[msg.queryLogId!] === r.code"
                    (click)="selectDataFeedbackReason(msg.queryLogId!, r.code)">
              {{ r.label }}
            </button>
          </div>

          <!-- Disambiguation candidates — clickable, like a search engine's "did you mean" -->
          <div class="ai-candidates" *ngIf="msg.role === 'assistant' && msg.candidates?.length">
            <button class="ai-candidate-chip" *ngFor="let c of msg.candidates" [disabled]="dataLoading"
                    (click)="selectCandidate(c)">
              <i class="pi pi-building"></i>
              <span>{{ c.name }}<ng-container *ngIf="c.contactPerson"> ({{ c.contactPerson }})</ng-container></span>
            </button>
          </div>

          <!-- View Details — only when the answer actually points somewhere real -->
          <button class="ai-view-details-btn" *ngIf="msg.role === 'assistant' && msg.deepLink"
                  (click)="goToDeepLink(msg.deepLink)">
            <span>View Details</span>
            <i class="pi pi-arrow-right"></i>
          </button>

          <!-- "You might also ask" — added 2026-08-23. Deliberately a different look from
               .ai-candidates above (that row resolves a pending question; this one starts a new
               one) so the two are never visually confused. Only ever present when there's nothing
               else pending — see the backend's own note on the gating rule. -->
          <div class="ai-related-questions" *ngIf="msg.role === 'assistant' && msg.relatedQuestions?.length">
            <span class="ai-related-questions-label">You might also ask</span>
            <button class="ai-related-question-chip" *ngFor="let q of msg.relatedQuestions" [disabled]="dataLoading"
                    (click)="askRelatedQuestion(q)">
              <i class="pi pi-plus-circle"></i>
              <span>{{ q }}</span>
            </button>
          </div>

          <!-- Feedback — small-screen row fallback only (hidden by default, see the
               max-width:420px media query in the CSS); same handlers as the inline version above -->
          <div class="ai-feedback" *ngIf="msg.role === 'assistant' && msg.resolved && msg.queryLogId">
            <button class="ai-fb-btn"
                    [class.ai-fb-btn--active]="dataFeedbackMap[msg.queryLogId!] === 1"
                    (click)="submitDataFeedback(msg.queryLogId!, 1)"
                    title="Helpful">
              <i class="pi pi-thumbs-up"></i>
            </button>
            <button class="ai-fb-btn"
                    [class.ai-fb-btn--active]="dataFeedbackMap[msg.queryLogId!] === -1"
                    (click)="submitDataFeedback(msg.queryLogId!, -1)"
                    title="Not helpful">
              <i class="pi pi-thumbs-down"></i>
            </button>
          </div>
          <div class="ai-fb-reason-row" *ngIf="activeDataReasonPromptId === msg.queryLogId">
            <button class="ai-fb-reason-chip" *ngFor="let r of feedbackReasons"
                    [class.ai-fb-reason-chip--active]="dataReasonMap[msg.queryLogId!] === r.code"
                    (click)="selectDataFeedbackReason(msg.queryLogId!, r.code)">
              {{ r.label }}
            </button>
          </div>
        </div>

        <!-- User avatar — real profile photo when set, else the real first-letter initial -->
        <ng-container *ngIf="msg.role === 'user'">
          <div class="ai-avatar-user ai-avatar-user--photo" *ngIf="getAvatarImgSrc() as avatarSrc; else userInitialAvatar">
            <img [src]="avatarSrc" alt="You">
          </div>
          <ng-template #userInitialAvatar>
            <div class="ai-avatar-user">{{ userInitial }}</div>
          </ng-template>
        </ng-container>
      </div>

      <!-- Typing indicator -->
      <div class="ai-msg-wrapper ai-msg-wrapper--bot" *ngIf="dataLoading">
        <div class="ai-avatar-bot-sm ai-avatar-bot-sm--breathing ai-avatar-bot-sm--data">
          <img class="ai-avatar-icon-img" src="assets/images/ai-chat/linc-ai-assistant-icon.png" alt="Linc AI Assistant">
        </div>
        <div class="ai-msg-bubble ai-msg-bubble--bot ai-msg-bubble--data ai-typing">
          <span class="ai-dot"></span>
          <span class="ai-dot"></span>
          <span class="ai-dot"></span>
        </div>
      </div>
    </div>

    <!-- ── Welcome screen — Auto mode (the single Linc box) ── -->
    <div class="ai-welcome" *ngIf="mode === 'auto' && autoThread.length === 0 && !autoLoading">

      <div class="ai-welcome-avatar-wrap">
        <div class="ai-welcome-orb">
          <img class="ai-welcome-icon-img" src="assets/images/ai-chat/linc-ai-help-icon.png" alt="Linc AI">
        </div>
        <span class="ai-welcome-sparkle ai-welcome-sparkle--1" aria-hidden="true">✦</span>
        <span class="ai-welcome-sparkle ai-welcome-sparkle--2" aria-hidden="true">✦</span>
        <span class="ai-welcome-sparkle ai-welcome-sparkle--3" aria-hidden="true">✦</span>
      </div>

      <div class="ai-welcome-greeting">{{ 'ai.chat.autoGreeting' | t }}</div>
      <div class="ai-welcome-title">{{ 'ai.chat.autoTitle' | t }}</div>
      <div class="ai-welcome-desc">{{ 'ai.chat.autoDesc' | t }}</div>

      <div class="ai-disclaimer-note">
        <i class="pi pi-shield"></i>
        {{ 'ai.chat.disclaimer' | t }}
      </div>

      <div class="ai-starters">
        <button class="ai-starter-chip" *ngFor="let s of autoStarters" [disabled]="autoLoading" (click)="sendStarter(starterQuestion(s.qKey))">
          <i [class]="s.icon"></i> {{ s.labelKey | t }}
        </button>
      </div>
    </div>

    <!-- ── Messages — Auto mode ──
         One merged thread. Each row is the same message object that also sits in the Help thread or
         the Data thread, so the Help and Assistant tabs still show their half of this conversation.
         The two bubble styles below are copies of the Help and Assistant bubbles above (same classes,
         same handlers), only reading from the merged row instead of their own list. -->
    <div class="ai-messages" #messagesContainer *ngIf="mode === 'auto' && autoThread.length > 0">
      <ng-container *ngFor="let item of autoThread; let i = index; trackBy: trackByAutoItem">

        <!-- Help-style bubble: the person's own messages, and every how-to / access answer -->
        <div *ngIf="item.kb as msg"
             class="ai-msg-wrapper"
             [class.ai-msg-wrapper--user]="msg.role === 'user'"
             [class.ai-msg-wrapper--bot]="msg.role === 'assistant'"
             [style.display]="msg.content?.trim() ? null : 'none'"
             [style.animation-delay.ms]="i * 25">

          <div class="ai-avatar-bot-sm" *ngIf="msg.role === 'assistant'">
            <img class="ai-avatar-icon-img" src="assets/images/ai-chat/linc-ai-help-icon.png" alt="Linc AI Help">
          </div>

          <div class="ai-msg-bubble"
               [class.ai-msg-bubble--user]="msg.role === 'user'"
               [class.ai-msg-bubble--bot]="msg.role === 'assistant'">

            <div class="ai-attachment-preview" *ngIf="msg.attachmentName">
              <i class="pi pi-file"></i>
              <span>{{ msg.attachmentName }}</span>
            </div>

            <div class="ai-msg-text" [innerHTML]="formatMessage(msg.content)"></div>

            <div class="ai-msg-meta">
              <span class="ai-msg-time">{{ formatMsgTime(msg.createdAt) }}</span>
              <ng-container *ngIf="msg.role === 'assistant' && msg.creditsUsed">
                <span class="ai-msg-meta-sep">&middot;</span>
                <span class="ai-credits-used">{{ msg.creditsUsed }} credit{{ msg.creditsUsed === 1 ? '' : 's' }} used</span>
              </ng-container>
              <ng-container *ngIf="msg.role === 'assistant' && msg.id">
                <span class="ai-msg-meta-sep">&middot;</span>
                <span class="ai-feedback--inline">
                  <button class="ai-fb-btn-inline"
                          [class.ai-fb-btn-inline--active]="feedbackMap[msg.id] === 1"
                          (click)="submitFeedback(msg.id!, 1)"
                          title="Helpful">
                    <i class="pi pi-thumbs-up"></i>
                  </button>
                  <button class="ai-fb-btn-inline"
                          [class.ai-fb-btn-inline--active]="feedbackMap[msg.id] === -1"
                          (click)="submitFeedback(msg.id!, -1)"
                          title="Not helpful">
                    <i class="pi pi-thumbs-down"></i>
                  </button>
                </span>
              </ng-container>
            </div>

            <div class="ai-fb-reason-row" *ngIf="activeReasonPromptId === msg.id">
              <button class="ai-fb-reason-chip" *ngFor="let r of feedbackReasons"
                      [class.ai-fb-reason-chip--active]="reasonMap[msg.id!] === r.code"
                      (click)="selectFeedbackReason(msg.id!, r.code)">
                {{ r.label }}
              </button>
            </div>

            <div class="ai-sources" *ngIf="msg.role === 'assistant' && item.sources.length">
              <span class="ai-sources-label">{{ 'ai.chat.sources' | t }}:</span>
              <span class="ai-source-chip" *ngFor="let src of item.sources">
                <i class="pi pi-database" style="font-size:.58rem"></i> {{ src }}
              </span>
            </div>

            <!-- Only the how-to half of a mixed question could be answered -->
            <div class="ai-auto-note" *ngIf="msg.role === 'assistant' && item.note === 'DATA_PART_FAILED'">
              <i class="pi pi-info-circle"></i>
              <span>{{ 'ai.chat.dataPartFailed' | t }}</span>
            </div>

            <!-- Go to screen — only when the answer points at a screen this person may open -->
            <button class="ai-view-details-btn" *ngIf="msg.role === 'assistant' && msg.deepLink"
                    (click)="goToDeepLink(msg.deepLink)">
              <span>{{ 'ai.chat.goToScreen' | t }}</span>
              <i class="pi pi-arrow-right"></i>
            </button>

            <div class="ai-related-questions" *ngIf="msg.role === 'assistant' && msg.relatedQuestions?.length">
              <span class="ai-related-questions-label">You might also ask</span>
              <button class="ai-related-question-chip" *ngFor="let q of msg.relatedQuestions" [disabled]="autoLoading"
                      (click)="askKbRelatedQuestion(q)">
                <i class="pi pi-plus-circle"></i>
                <span>{{ q }}</span>
              </button>
            </div>

            <div class="ai-feedback" *ngIf="msg.role === 'assistant' && msg.id">
              <button class="ai-fb-btn"
                      [class.ai-fb-btn--active]="feedbackMap[msg.id] === 1"
                      (click)="submitFeedback(msg.id!, 1)"
                      title="Helpful">
                <i class="pi pi-thumbs-up"></i>
              </button>
              <button class="ai-fb-btn"
                      [class.ai-fb-btn--active]="feedbackMap[msg.id] === -1"
                      (click)="submitFeedback(msg.id!, -1)"
                      title="Not helpful">
                <i class="pi pi-thumbs-down"></i>
              </button>
            </div>
          </div>

          <ng-container *ngIf="msg.role === 'user'">
            <div class="ai-avatar-user ai-avatar-user--photo" *ngIf="getAvatarImgSrc() as avatarSrc; else autoUserInitialAvatar">
              <img [src]="avatarSrc" alt="You">
            </div>
            <ng-template #autoUserInitialAvatar>
              <div class="ai-avatar-user">{{ userInitial }}</div>
            </ng-template>
          </ng-container>
        </div>

        <!-- Data bubble: every live-data answer -->
        <div *ngIf="item.data as msg"
             class="ai-msg-wrapper"
             [class.ai-msg-wrapper--user]="msg.role === 'user'"
             [class.ai-msg-wrapper--bot]="msg.role === 'assistant'"
             [style.display]="msg.content?.trim() ? null : 'none'"
             [style.animation-delay.ms]="i * 25">

          <div class="ai-avatar-bot-sm ai-avatar-bot-sm--data" *ngIf="msg.role === 'assistant'">
            <img class="ai-avatar-icon-img" src="assets/images/ai-chat/linc-ai-assistant-icon.png" alt="Linc AI Assistant">
          </div>

          <div class="ai-msg-bubble ai-msg-bubble--data"
               [class.ai-msg-bubble--user]="msg.role === 'user'"
               [class.ai-msg-bubble--bot]="msg.role === 'assistant'">

            <div class="ai-msg-text" [ngSwitch]="msg.formatted.kind">

              <div *ngSwitchCase="'text'" [innerHTML]="msg.formatted.html"></div>

              <div *ngSwitchCase="'table'" class="ai-ans-table-wrap">
                <p-table [value]="msg.formatted.rows" [columns]="msg.formatted.columns" styleClass="ai-ans-table">
                  <ng-template pTemplate="header" let-columns>
                    <tr>
                      <th *ngFor="let col of columns">{{ col.label }}</th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-row let-rowIndex="rowIndex" let-columns="columns">
                    <tr [style.animation-delay.ms]="rowIndex * 40">
                      <td *ngFor="let col of columns">{{ row[col.key] }}</td>
                    </tr>
                  </ng-template>
                </p-table>
              </div>

              <div *ngSwitchCase="'ranked'" class="ai-ans-ranked">
                <div class="ai-ans-ranked-row" *ngFor="let ranked of msg.formatted.items; let j = index"
                     [style.animation-delay.ms]="j * 40">
                  <p-badge [value]="ranked.rank" severity="secondary"></p-badge>
                  <span class="ai-ans-ranked-name">{{ ranked.companyName }}</span>
                  <p-tag *ngIf="ranked.onTimeLabel" [value]="ranked.onTimeLabel" [severity]="ranked.onTimeSeverity ?? undefined"></p-tag>
                  <p-tag *ngIf="ranked.lossLabel" [value]="ranked.lossLabel" [severity]="ranked.lossSeverity ?? undefined"></p-tag>
                  <span class="ai-ans-ranked-extra" *ngIf="ranked.outturnLabel">{{ ranked.outturnLabel }}</span>
                  <span class="ai-ans-ranked-extra">{{ ranked.shipmentCount }} shipment{{ ranked.shipmentCount === 1 ? '' : 's' }}</span>
                </div>
              </div>

              <div *ngSwitchCase="'stat'" class="ai-ans-stats">
                <div class="ai-ans-stat-tile" *ngFor="let stat of msg.formatted.items; let j = index"
                     [style.animation-delay.ms]="j * 40">
                  <div class="ai-ans-stat-value">{{ stat.value }}</div>
                  <div class="ai-ans-stat-label">{{ stat.label }}</div>
                </div>
              </div>

            </div>

            <div class="ai-msg-meta">
              <span class="ai-msg-time">{{ formatMsgTime(msg.createdAt) }}</span>
              <ng-container *ngIf="msg.role === 'assistant' && msg.creditsUsed">
                <span class="ai-msg-meta-sep">&middot;</span>
                <span class="ai-credits-used" [class.ai-credits-pulse]="msg.creditsAnimating">{{ msg.creditsUsed }} credit{{ msg.creditsUsed === 1 ? '' : 's' }} used<span class="ai-reward-float" *ngIf="msg.creditsRewardBadgeAmount">+{{ msg.creditsRewardBadgeAmount }} credits back &#10024;</span></span>
              </ng-container>
              <ng-container *ngIf="msg.role === 'assistant' && msg.resolved && msg.queryLogId">
                <span class="ai-msg-meta-sep">&middot;</span>
                <span class="ai-feedback--inline">
                  <button class="ai-fb-btn-inline"
                          [class.ai-fb-btn-inline--active]="dataFeedbackMap[msg.queryLogId!] === 1"
                          (click)="submitDataFeedback(msg.queryLogId!, 1)"
                          title="Helpful">
                    <i class="pi pi-thumbs-up"></i>
                  </button>
                  <button class="ai-fb-btn-inline"
                          [class.ai-fb-btn-inline--active]="dataFeedbackMap[msg.queryLogId!] === -1"
                          (click)="submitDataFeedback(msg.queryLogId!, -1)"
                          title="Not helpful">
                    <i class="pi pi-thumbs-down"></i>
                  </button>
                </span>
              </ng-container>
            </div>

            <div class="ai-fb-reason-row" *ngIf="activeDataReasonPromptId === msg.queryLogId">
              <button class="ai-fb-reason-chip" *ngFor="let r of feedbackReasons"
                      [class.ai-fb-reason-chip--active]="dataReasonMap[msg.queryLogId!] === r.code"
                      (click)="selectDataFeedbackReason(msg.queryLogId!, r.code)">
                {{ r.label }}
              </button>
            </div>

            <div class="ai-candidates" *ngIf="msg.role === 'assistant' && msg.candidates?.length">
              <button class="ai-candidate-chip" *ngFor="let c of msg.candidates" [disabled]="autoLoading"
                      (click)="selectCandidate(c)">
                <i class="pi pi-building"></i>
                <span>{{ c.name }}<ng-container *ngIf="c.contactPerson"> ({{ c.contactPerson }})</ng-container></span>
              </button>
            </div>

            <button class="ai-view-details-btn" *ngIf="msg.role === 'assistant' && msg.deepLink"
                    (click)="goToDeepLink(msg.deepLink)">
              <span>View Details</span>
              <i class="pi pi-arrow-right"></i>
            </button>

            <div class="ai-related-questions" *ngIf="msg.role === 'assistant' && msg.relatedQuestions?.length">
              <span class="ai-related-questions-label">You might also ask</span>
              <button class="ai-related-question-chip" *ngFor="let q of msg.relatedQuestions" [disabled]="autoLoading"
                      (click)="askRelatedQuestion(q)">
                <i class="pi pi-plus-circle"></i>
                <span>{{ q }}</span>
              </button>
            </div>

            <div class="ai-feedback" *ngIf="msg.role === 'assistant' && msg.resolved && msg.queryLogId">
              <button class="ai-fb-btn"
                      [class.ai-fb-btn--active]="dataFeedbackMap[msg.queryLogId!] === 1"
                      (click)="submitDataFeedback(msg.queryLogId!, 1)"
                      title="Helpful">
                <i class="pi pi-thumbs-up"></i>
              </button>
              <button class="ai-fb-btn"
                      [class.ai-fb-btn--active]="dataFeedbackMap[msg.queryLogId!] === -1"
                      (click)="submitDataFeedback(msg.queryLogId!, -1)"
                      title="Not helpful">
                <i class="pi pi-thumbs-down"></i>
              </button>
            </div>
          </div>
        </div>

      </ng-container>

      <!-- Typing indicator -->
      <div class="ai-msg-wrapper ai-msg-wrapper--bot" *ngIf="autoLoading">
        <div class="ai-avatar-bot-sm ai-avatar-bot-sm--breathing">
          <img class="ai-avatar-icon-img" src="assets/images/ai-chat/linc-ai-help-icon.png" alt="Linc AI">
        </div>
        <div class="ai-msg-bubble ai-msg-bubble--bot ai-typing">
          <span class="ai-dot"></span>
          <span class="ai-dot"></span>
          <span class="ai-dot"></span>
        </div>
      </div>
    </div>

    </ng-container>

  </div><!-- end ai-chat-body -->

  <!-- ── Footer ──────────────────────────────────────────────────────── -->
  <ng-template pTemplate="footer">
    <!-- Disclaimer gate footer — the accept control is pinned here (not inside the scrollable
         body text) so it's always visible without hunting for it at the bottom of a long read. -->
    <div class="ai-footer" *ngIf="disclaimerAccepted === false">
      <label class="ai-disclaimer-gate-check">
        <input type="checkbox" [(ngModel)]="disclaimerChecked">
        <span>I have read, understood, and accept the above.</span>
      </label>
      <button class="ai-disclaimer-gate-btn" [disabled]="!disclaimerChecked || disclaimerAccepting"
              (click)="acceptDisclaimer()">
        <span *ngIf="!disclaimerAccepting">I Understand &amp; Accept — Continue</span>
        <span *ngIf="disclaimerAccepting">Saving…</span>
      </button>
    </div>

    <div class="ai-footer" *ngIf="disclaimerAccepted">

      <!-- Attachment strip (Linc/KB mode only — "Linc Data" doesn't accept attachments) -->
      <div class="ai-attachment-strip" *ngIf="mode !== 'data' && pendingAttachment">
        <i class="pi pi-file-image" *ngIf="isImage(pendingAttachment.type)"></i>
        <i class="pi pi-file"       *ngIf="!isImage(pendingAttachment.type)"></i>
        <span>{{ pendingAttachment.name }}</span>
        <button class="ai-attachment-remove" (click)="clearAttachment()">
          <i class="pi pi-times"></i>
        </button>
      </div>

      <!-- Locked states (Linc/KB mode only — "Linc Data" has its own credit/access errors shown
           inline as a chat bubble instead of a persistent footer lock) -->
      <div class="ai-footer-locked" *ngIf="mode === 'kb' && status.blocked">
        <i class="pi pi-ban"></i>
        <span>Chat blocked — try again after the block expires</span>
      </div>
      <div class="ai-footer-locked ai-footer-locked--warning" *ngIf="mode === 'kb' && !status.blocked && dailyLimitExhausted">
        <i class="pi pi-clock"></i>
        <span>Daily limit reached — resets at midnight</span>
      </div>

      <!-- Input row -->
      <div class="ai-input-row" *ngIf="mode !== 'kb' || (!status.blocked && !dailyLimitExhausted)">
        <button class="ai-input-btn" (click)="emojiPanel.toggle($event)" title="Emoji">
          <i class="pi pi-face-smile"></i>
        </button>
        <app-media-capture-button
          *ngIf="mode !== 'data'"
          class="ai-input-capture-btn"
          label=""
          buttonIcon="pi pi-paperclip"
          acceptTypes="image/*,.pdf"
          [multiple]="false"
          (filesSelected)="onMediaCaptured($event)"
        ></app-media-capture-button>
        <textarea
          #inputArea
          class="ai-textarea"
          [(ngModel)]="inputText"
          [placeholder]="mode === 'data' ? 'Ask about your stock, shipments, payments…' : (mode === 'auto' ? ('ai.chat.autoPlaceholder' | t) : ('ai.chat.placeholder' | t))"
          rows="1"
          enterkeyhint="send"
          [disabled]="isBusy"
          (keydown)="onKeyDown($event)"
          (input)="autoResize($event)">
        </textarea>
        <button class="ai-input-btn ai-sound-btn"
                (click)="toggleSound()"
                [title]="soundEnabled ? 'Mute sounds' : 'Enable sounds'">
          <i [class]="soundEnabled ? 'pi pi-volume-up' : 'pi pi-volume-off'"></i>
        </button>
        <button class="ai-send-btn"
                (click)="sendMessage()"
                [disabled]="isBusy || !inputText.trim()"
                [class.ai-send-btn--active]="!!inputText.trim()">
          <i class="pi pi-send"></i>
        </button>
      </div>

      <!-- Was two separate lines (a safety reminder + a branding/attribution line) — merged into
           one, deliberately small and plain rather than styled like the rest of this widget, the
           way real disclaimer/fine-print text usually looks (independent of the surrounding
           brand's font/accent color, not part of the chat UI itself). -->
      <div class="ai-mini-disclaimer">*AI can make mistakes.</div>
    </div>
  </ng-template>
</p-drawer>

<!-- ── Emoji panel ─────────────────────────────────────────────────────── -->
<p-popover #emojiPanel styleClass="ai-emoji-panel">
  <div class="ai-emoji-grid">
    <button *ngFor="let emoji of emojis" class="ai-emoji-btn" (click)="insertEmoji(emoji, emojiPanel)">
      {{ emoji }}
    </button>
  </div>
</p-popover>

<!-- ── Email transcript dialog ─────────────────────────────────────────── -->
<p-dialog
  header="Share Chat Transcript"
  [(visible)]="showEmailDialog"
  [modal]="true"
  [style]="{'width':'390px'}"
  [draggable]="false"
  appendTo="body">
  <div class="flex flex-column gap-3 py-2">
    <div class="ai-transcript-info">
      <div class="ai-transcript-icon">
        <i class="pi pi-envelope"></i>
      </div>
      <span class="text-sm" style="color:var(--text-color-secondary);line-height:1.5">
        We'll email a full transcript of this Linc conversation to the address below.
      </span>
    </div>
    <div>
      <label class="text-sm font-semibold block mb-2">Recipient email</label>
      <input pInputText [(ngModel)]="transcriptEmail" placeholder="your@email.com" class="ai-transcript-input w-full">
    </div>
  </div>
  <ng-template pTemplate="footer">
    <p-button label="Cancel" styleClass="p-button-text p-button-plain" (click)="showEmailDialog = false"></p-button>
    <p-button label="Send Transcript" icon="pi pi-send" (click)="sendTranscript()"></p-button>
  </ng-template>
</p-dialog>
